import httpClient from 'services/httpClient';
import { ApiResponse } from 'services/api';
import type {
  CoursePlanBusinessUnit,
  CoursePlanJobFamilyGroup,
  CoursePlanCourse,
  CoursePlanTargetPosition,
  CoursePlanJobFamily
} from '../../types/lcms/coursePlan';

export interface CoursePlanData {
  msLCMSPlanningID: string;
  planningName: string;
  programType: 'modular' | 'program' | 'emerging needs';
  planningDescription: string;
  planningStart: string;
  planningEnd: string;
  prerequisites: string;
  learningMaterials: string;
  facilitiesRequired: string;
  notes: string;
  userIn: string;
  userUp: string | null;
  dateIn: string;
  dateUp: string | null;
  isDeleted: boolean;
  totalCourses: number;
  totalPositions: number;
  totalBusinessUnits: number;
  totalJobFamilyGroups: number;
}

export interface TargetPosition {
  positionID: string;
  positionName: string;
  description?: string;
  level?: string;
}

export interface CoursePlanFilters {
  search?: string;
  programType?: string;
  startDate?: string;
  endDate?: string;
  businessUnitId?: string;
  jobFamilyGroupId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateCoursePlanDto {
  planningName: string;
  programType: 'modular' | 'program' | 'emerging needs';
  planningDescription: string;
  planningStart: string;
  planningEnd: string;
  prerequisites: string;
  learningMaterials: string;
  facilitiesRequired: string;
  notes: string;
  courses: Array<{
    courseId: string;
    modeOfDelivery: string;
  }>;
  targetPositions?: Array<{
    positionID: string;
    positionName: string;
  }>;
  businessUnits?: Array<{
    businessUnitID: string;
    businessUnitName: string;
  }>;
  jobFamilyGroups?: Array<{
    jobFamilyGroupID: string;
    jobFamilyGroupName: string;
  }>;
  jobFamilies?: Array<{
    jobFamilyID: string;
    jobFamilyName: string;
    jobFamilyGroupID: string;
    jobFamilyGroupName: string;
  }>;
}

export interface UpdateCoursePlanDto extends CreateCoursePlanDto {
  msLCMSPlanningID: string;
}

export interface CoursePlanDetailData {
  msLCMSPlanningID: string;
  planningName: string;
  programType: 'modular' | 'program' | 'emerging needs';
  planningDescription: string;
  planningStart: string;
  planningEnd: string;
  prerequisites: string;
  learningMaterials: string;
  facilitiesRequired: string;
  notes: string;
  userIn: string;
  userUp: string | null;
  dateIn: string;
  dateUp: string | null;
  isDeleted: boolean;
  courses: CoursePlanCourse[];
  targetPositions: CoursePlanTargetPosition[];
  businessUnits: CoursePlanBusinessUnit[];
  jobFamilyGroups: CoursePlanJobFamilyGroup[];
  jobFamilies: CoursePlanJobFamily[];
}
export interface CourseModularData {
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  deliveryType: string;
  learningHours: number;
  courseCategory: string;
  description: string;
  modeOfDelivery?: string;
}

export interface CourseProgramData {
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  deliveryType: string;
  learningHours: number;
  courseCategory: string;
  description: string;
  modeOfDelivery?: string;
}

export interface CourseEmergingNeedsData {
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  deliveryType: string;
  learningHours: number;
  courseCategory: string;
  description: string;
  modeOfDelivery?: string;
}

interface GetCoursePlanListParams {
  search?: string;
  programType?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  pageNumber?: number;
  pageSize?: number;
}

export interface DACUMProgramData {
  msDACUMProgramID: string;
  msLCMSCourseID: string;
  description: string;
  courseName: string;
  courseCode: string;
  deliveryType?: string;
  learningHours?: number;
  courseCategory?: string;
}

export interface LCMSBusinessUnit {
  buCode: string;
  buName: string;
}

export interface LCMSJobFamilyGroup {
  jobFamilyGroupID: string;
  jobFamilyGroupName: string;
}

export interface LCMSJobFamily {
  jobFamilyID: string;
  jobFamilyName: string;
}

export interface LCMSJobPosition {
  positionID?: string;
  positionId?: string;
  positionName?: string;
  buCode?: string;
  BuCode?: string;
  jobFamilyGroupID?: string;
  JobFamilyGroupID?: string;
  jobFamilyID?: string;
  JobFamilyID?: string;
  namaJabatan: string;
}

class CoursePlanService {
  private baseUrl = '/lcms/planning';

  async getCoursePlanList(
    params: GetCoursePlanListParams = {}
  ): Promise<ApiResponse<CoursePlanData>> {
    try {
      const queryParams = new URLSearchParams({
        pageNumber: (params.pageNumber || 1).toString(),
        pageSize: (params.pageSize || 10).toString(),
        search: params.search || '',
        programType: params.programType || '',
        sortBy: params.sortBy || 'datein',
        sortOrder: params.sortOrder || 'desc',
        status: params.status || ''
      });

      if (params.startDate) {
        queryParams.set('startDate', params.startDate);
      }
      if (params.endDate) {
        queryParams.set('endDate', params.endDate);
      }

      const response = await httpClient.get<ApiResponse<CoursePlanData>>(
        `${this.baseUrl}/get-list?${queryParams.toString()}`
      );

      return response.data;
    } catch {
      const pageNumber = params.pageNumber || 1;
      const pageSize = params.pageSize || 10;
      const filters: CoursePlanFilters = {
        search: params.search,
        programType: params.programType,
        startDate: params.startDate,
        endDate: params.endDate,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder
      };

      return this.getMockCoursePlanList(pageNumber, pageSize, filters);
    }
  }

  async getTargetPositions(
    pageNumber: number = 1,
    pageSize: number = 100,
    search?: string
  ): Promise<ApiResponse<LCMSJobPosition>> {
    try {
      const params = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        ...(search && { search })
      });

      const response = await httpClient.get<ApiResponse<LCMSJobPosition>>(
        `/lcms/job-positions?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getTargetPositions API failed:', error);
      return {
        pageNumber,
        pageSize,
        totalPages: 0,
        totalRecords: 0,
        message: 'No data found',
        data: []
      };
    }
  }

  async getCoursePlanDetail(id: string): Promise<{ message: string; data: CoursePlanDetailData }> {
    try {
      const response = await httpClient.get(`${this.baseUrl}/detail/${id}`);
      return response.data;
    } catch {
      return this.getMockCoursePlanDetail(id);
    }
  }

  async createCoursePlan(data: CreateCoursePlanDto): Promise<{ message: string; data: any }> {
    const response = await httpClient.post(`${this.baseUrl}/add`, data);
    return response.data;
  }

  // Update course plan
  async updateCoursePlan(data: UpdateCoursePlanDto): Promise<{ message: string; data: any }> {
    const response = await httpClient.put(`${this.baseUrl}/update`, data);
    return response.data;
  }

  // Delete course plan
  async deleteCoursePlan(id: string): Promise<{ message: string }> {
    try {
      const response = await httpClient.delete(`${this.baseUrl}/delete`, {
        data: { msLCMSPlanningID: id }
      });
      return response.data;
    } catch {
      return { message: 'Planning deleted successfully' };
    }
  }

  async getBusinessUnits(
    pageNumber: number = 1,
    pageSize: number = 100,
    search?: string
  ): Promise<ApiResponse<LCMSBusinessUnit>> {
    try {
      const params = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        search: search || ''
      });

      const response = await httpClient.get<ApiResponse<LCMSBusinessUnit>>(
        `/lcms/business-units?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getBusinessUnits API failed:', error);
      return {
        pageNumber,
        pageSize,
        totalPages: 0,
        totalRecords: 0,
        message: 'No data found',
        data: []
      };
    }
  }

  async getJobFamilyGroups(
    pageNumber: number = 1,
    pageSize: number = 100,
    search?: string,
    businessUnitIds?: string[]
  ): Promise<ApiResponse<LCMSJobFamilyGroup>> {
    try {
      const params = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        search: search || ''
      });

      if (businessUnitIds && businessUnitIds.length > 0) {
        params.set('bu', businessUnitIds.join(','));
      }

      const response = await httpClient.get<ApiResponse<LCMSJobFamilyGroup>>(
        `/lcms/job-family-groups?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getJobFamilyGroups API failed:', error);
      return {
        pageNumber,
        pageSize,
        totalPages: 0,
        totalRecords: 0,
        message: 'No data found',
        data: []
      };
    }
  }

  async getJobFamilies(
    jobFamilyGroupID: string,
    pageNumber: number = 1,
    pageSize: number = 100,
    search?: string
  ): Promise<ApiResponse<LCMSJobFamily>> {
    try {
      const params = new URLSearchParams({
        jobFamilyGroupID,
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString()
      });

      if (search) {
        params.append('search', search);
      }

      const response = await httpClient.get<ApiResponse<LCMSJobFamily>>(
        `/lcms/job-families?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getJobFamilies API failed:', error);
      return {
        pageNumber,
        pageSize,
        totalPages: 0,
        totalRecords: 0,
        message: 'No data found',
        data: []
      };
    }
  }

  async getJobPositions(
    pageNumber: number = 1,
    pageSize: number = 100,
    search?: string,
    businessUnitIds?: string[],
    jobFamilyGroupIds?: string[],
    jobFamilyIds?: string[],
    planningId?: string
  ): Promise<ApiResponse<LCMSJobPosition>> {
    try {
      const params = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        search: search || ''
      });

      // Add business unit IDs if provided
      if (businessUnitIds && businessUnitIds.length > 0) {
        params.set('bu', businessUnitIds.join(','));
      }

      // Add job family group IDs if provided
      if (jobFamilyGroupIds && jobFamilyGroupIds.length > 0) {
        params.set('jobfamilygroupid', jobFamilyGroupIds.join(','));
      }

      // Add job family IDs if provided
      if (jobFamilyIds && jobFamilyIds.length > 0) {
        params.set('jobfamilyid', jobFamilyIds.join(','));
      }

      if (planningId) {
        params.append('planningId', planningId);
      }

      const response = await httpClient.get<ApiResponse<LCMSJobPosition>>(
        `/lcms/job-positions?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getJobPositions API failed:', error);
      return {
        pageNumber,
        pageSize,
        totalPages: 0,
        totalRecords: 0,
        message: 'No data found',
        data: []
      };
    }
  }

  async getModularCourses(
    pageNumber: number = 1,
    pageSize: number = 100,
    search?: string,
    jobFamilyGroupIds?: string[],
    jobFamilyIds?: string[],
    businessUnitIds?: string[],
    jobPositionIds?: string[]
  ): Promise<ApiResponse<CourseModularData>> {
    try {
      const params = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        ...(search && { search })
      });

      if (jobFamilyGroupIds && jobFamilyGroupIds.length > 0) {
        params.append('jobfamgroupid', jobFamilyGroupIds.join(','));
      }
      if (jobFamilyIds && jobFamilyIds.length > 0) {
        params.append('jobfamid', jobFamilyIds.join(','));
      }
      if (businessUnitIds && businessUnitIds.length > 0) {
        params.append('businessunitid', businessUnitIds.join(','));
      }
      if (jobPositionIds && jobPositionIds.length > 0) {
        params.append('jobpositionid', jobPositionIds.join(','));
      }

      const response = await httpClient.get<ApiResponse<CourseModularData>>(
        `/lcms/courses/modular?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getModularCourses API failed:', error);
      return {
        pageNumber,
        pageSize,
        totalPages: 0,
        totalRecords: 0,
        message: 'No data found',
        data: []
      };
    }
  }

  async getProgramCourses(
    pageNumber: number = 1,
    pageSize: number = 100,
    search?: string,
    jobFamilyGroupIds?: string[],
    jobFamilyIds?: string[],
    businessUnitIds?: string[],
    jobPositionIds?: string[]
  ): Promise<ApiResponse<CourseProgramData>> {
    try {
      const params = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        ...(search && { search })
      });

      if (jobFamilyGroupIds && jobFamilyGroupIds.length > 0) {
        params.append('jobfamgroupid', jobFamilyGroupIds.join(','));
      }
      if (jobFamilyIds && jobFamilyIds.length > 0) {
        params.append('jobfamid', jobFamilyIds.join(','));
      }
      if (businessUnitIds && businessUnitIds.length > 0) {
        params.append('businessunitid', businessUnitIds.join(','));
      }
      if (jobPositionIds && jobPositionIds.length > 0) {
        params.append('jobpositionid', jobPositionIds.join(','));
      }

      const response = await httpClient.get<ApiResponse<CourseProgramData>>(
        `/lcms/courses/program?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getProgramCourses API failed:', error);
      return {
        pageNumber,
        pageSize,
        totalPages: 0,
        totalRecords: 0,
        message: 'No data found',
        data: []
      };
    }
  }

  async getDACUMPrograms(
    pageNumber: number = 1,
    pageSize: number = 100,
    search?: string,
    jobFamilyGroupIds?: string[],
    jobFamilyIds?: string[],
    businessUnitIds?: string[],
    jobPositionIds?: string[]
  ): Promise<ApiResponse<DACUMProgramData>> {
    try {
      const params = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        ...(search && { search })
      });

      if (jobFamilyGroupIds && jobFamilyGroupIds.length > 0) {
        params.append('jobfamgroupid', jobFamilyGroupIds.join(','));
      }
      if (jobFamilyIds && jobFamilyIds.length > 0) {
        params.append('jobfamid', jobFamilyIds.join(','));
      }
      if (businessUnitIds && businessUnitIds.length > 0) {
        params.append('businessunitid', businessUnitIds.join(','));
      }
      if (jobPositionIds && jobPositionIds.length > 0) {
        params.append('jobpositionid', jobPositionIds.join(','));
      }

      const response = await httpClient.get<ApiResponse<DACUMProgramData>>(
        `/dacum/programs?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getDACUMPrograms API failed:', error);
      return {
        pageNumber,
        pageSize,
        totalPages: 0,
        totalRecords: 0,
        message: 'No data found',
        data: []
      };
    }
  }

  async getEmergingNeedsCourses(
    pageNumber: number = 1,
    pageSize: number = 100,
    search?: string,
    jobFamilyGroupIds?: string[],
    jobFamilyIds?: string[],
    businessUnitIds?: string[],
    jobPositionIds?: string[]
  ): Promise<ApiResponse<CourseEmergingNeedsData>> {
    try {
      const params = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        ...(search && { search })
      });

      if (jobFamilyGroupIds && jobFamilyGroupIds.length > 0) {
        params.append('jobfamgroupid', jobFamilyGroupIds.join(','));
      }
      if (jobFamilyIds && jobFamilyIds.length > 0) {
        params.append('jobfamid', jobFamilyIds.join(','));
      }
      if (businessUnitIds && businessUnitIds.length > 0) {
        params.append('businessunitid', businessUnitIds.join(','));
      }
      if (jobPositionIds && jobPositionIds.length > 0) {
        params.append('jobpositionid', jobPositionIds.join(','));
      }

      const response = await httpClient.get<ApiResponse<CourseEmergingNeedsData>>(
        `/lcms/courses/emerging-needs?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getEmergingNeedsCourses API failed:', error);
      return {
        pageNumber,
        pageSize,
        totalPages: 0,
        totalRecords: 0,
        message: 'No data found',
        data: []
      };
    }
  }

  private getMockTargetPositions(
    pageNumber: number,
    pageSize: number,
    search?: string
  ): ApiResponse<LCMSJobPosition> {
    const mockData: LCMSJobPosition[] = [
      { namaJabatan: 'Software Engineer' },
      { namaJabatan: 'Student Administration Services Staff' },
      { namaJabatan: 'Early Years and Primary Classroom Teacher' },
      { namaJabatan: 'Dean - BBS Undergraduate Program' },
      { namaJabatan: 'Marketing Manager - BINUS Business School' }
    ];

    let filteredData = mockData;
    if (search) {
      filteredData = mockData.filter((item) =>
        item.namaJabatan.toLowerCase().includes(search.toLowerCase())
      );
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredData.length / pageSize),
      totalRecords: filteredData.length,
      message: 'Success',
      data: paginatedData
    };
  }

  private getMockCoursePlanList(
    pageNumber: number,
    pageSize: number,
    filters: CoursePlanFilters
  ): ApiResponse<CoursePlanData> {
    const mockData: CoursePlanData[] = [
      {
        msLCMSPlanningID: 'uuid-1',
        planningName: 'Q1 Leadership Training',
        programType: 'modular',
        planningDescription: 'Leadership development program for managers',
        planningStart: '2024-01-15',
        planningEnd: '2024-03-15',
        prerequisites: 'Basic management experience',
        learningMaterials: 'Books, videos, case studies',
        facilitiesRequired: 'Training room, projector',
        notes: 'Special requirements noted',
        userIn: 'BINUS001',
        userUp: null,
        dateIn: '2024-01-01T10:00:00Z',
        dateUp: null,
        isDeleted: false,
        totalCourses: 5,
        totalPositions: 3,
        totalBusinessUnits: 2,
        totalJobFamilyGroups: 1
      },
      {
        msLCMSPlanningID: 'uuid-2',
        planningName: 'Technical Skills Assessment',
        programType: 'program',
        planningDescription: 'Advanced technical skills for developers',
        planningStart: '2024-02-01',
        planningEnd: '2024-04-30',
        prerequisites: 'Programming fundamentals',
        learningMaterials: 'Online courses, labs, documentation',
        facilitiesRequired: 'Computer lab, internet access',
        notes: 'Hands-on training required',
        userIn: 'BINUS002',
        userUp: null,
        dateIn: '2024-01-15T09:00:00Z',
        dateUp: null,
        isDeleted: false,
        totalCourses: 8,
        totalPositions: 2,
        totalBusinessUnits: 1,
        totalJobFamilyGroups: 2
      }
    ];

    let filteredData = mockData;
    if (filters.search) {
      filteredData = mockData.filter(
        (item) =>
          item.planningName.toLowerCase().includes(filters.search!.toLowerCase()) ||
          item.planningDescription.toLowerCase().includes(filters.search!.toLowerCase())
      );
    }
    if (filters.programType) {
      filteredData = filteredData.filter((item) => item.programType === filters.programType);
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredData.length / pageSize),
      totalRecords: filteredData.length,
      message: 'Success',
      data: paginatedData
    };
  }

  private getMockCoursePlanDetail(id: string): { message: string; data: CoursePlanDetailData } {
    return {
      message: 'Success',
      data: {
        msLCMSPlanningID: id,
        planningName: 'Q2 Technical Training New',
        programType: 'program',
        planningDescription: 'Advanced technical skills',
        planningStart: '2024-04-01T00:00:00',
        planningEnd: '2024-06-30T00:00:00',
        prerequisites: 'Basic programming knowledge',
        learningMaterials: 'Online courses, labs',
        facilitiesRequired: 'Computer lab',
        notes: 'Hands-on training',
        userIn: '000000',
        userUp: null,
        dateIn: '2025-07-11T14:51:24.547',
        dateUp: null,
        isDeleted: false,
        courses: [
          {
            trLCMSPlanningCoursesID: 'uuid-course-1',
            msLCMSCourseID: 'uuid-course-a',
            courseName: 'Leadership Fundamentals',
            courseCode: 'LEAD001',
            deliveryType: 'program',
            learningHours: 20
          }
        ],
        targetPositions: [
          {
            trLCMSPlanningTargetPositionsID: 'uuid-pos-1',
            positionID: 'DEV001',
            positionName: 'Developer'
          }
        ],
        businessUnits: [
          {
            trLCMSPlanningBusinessUnitsID: 'uuid-bu-1',
            msLCMSPlanningID: id,
            businessUnitID: 'BU001',
            businessUnitName: 'Information Technology'
          },
          {
            trLCMSPlanningBusinessUnitsID: 'uuid-bu-2',
            msLCMSPlanningID: id,
            businessUnitID: 'BU002',
            businessUnitName: 'Human Resources'
          }
        ],
        jobFamilyGroups: [
          {
            trLCMSPlanningJobFamilyGroupsID: 'uuid-jfg-1',
            msLCMSPlanningID: id,
            jobFamilyGroupID: 'JGF001',
            jobFamilyGroupName: 'Management'
          },
          {
            trLCMSPlanningJobFamilyGroupsID: 'uuid-jfg-2',
            msLCMSPlanningID: id,
            jobFamilyGroupID: 'JGF002',
            jobFamilyGroupName: 'Technical'
          }
        ],
        jobFamilies: [
          {
            trLCMSPlanningJobFamiliesID: 'uuid-jf-1',
            msLCMSPlanningID: id,
            jobFamilyID: 'JF001',
            jobFamilyName: 'Executive Management',
            jobFamilyGroupID: 'JGF001',
            jobFamilyGroupName: 'Management'
          },
          {
            trLCMSPlanningJobFamiliesID: 'uuid-jf-2',
            msLCMSPlanningID: id,
            jobFamilyID: 'JF002',
            jobFamilyName: 'Software Development',
            jobFamilyGroupID: 'JGF002',
            jobFamilyGroupName: 'Technical'
          }
        ]
      }
    };
  }

  private getMockBusinessUnits(
    pageNumber: number,
    pageSize: number,
    search?: string
  ): ApiResponse<LCMSBusinessUnit> {
    const mockData: LCMSBusinessUnit[] = [
      {
        buCode: '15000129',
        buName: 'Infrastructure Development&Optimization'
      },
      {
        buCode: 'S00000159',
        buName: 'Nawasena Cita Nusantara'
      },
      {
        buCode: 'IT001',
        buName: 'Information Technology'
      },
      {
        buCode: 'HR001',
        buName: 'Human Resources'
      }
    ];

    let filteredData = mockData;
    if (search) {
      filteredData = mockData.filter(
        (item) =>
          item.buName.toLowerCase().includes(search.toLowerCase()) ||
          item.buCode.toLowerCase().includes(search.toLowerCase())
      );
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredData.length / pageSize),
      totalRecords: filteredData.length,
      message: 'Data Found',
      data: paginatedData
    };
  }

  private getMockJobFamilyGroups(
    pageNumber: number,
    pageSize: number,
    search?: string
  ): ApiResponse<LCMSJobFamilyGroup> {
    const mockData: LCMSJobFamilyGroup[] = [
      {
        jobFamilyGroupID: 'JF0005',
        jobFamilyGroupName: 'Executive Management_JF'
      },
      {
        jobFamilyGroupID: 'JF0006',
        jobFamilyGroupName: 'Facilities Management & Services'
      },
      {
        jobFamilyGroupID: 'JF0019',
        jobFamilyGroupName: 'Knowledge & Information Management'
      }
    ];

    let filteredData = mockData;
    if (search) {
      filteredData = mockData.filter(
        (item) =>
          item.jobFamilyGroupName.toLowerCase().includes(search.toLowerCase()) ||
          item.jobFamilyGroupID.toLowerCase().includes(search.toLowerCase())
      );
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredData.length / pageSize),
      totalRecords: filteredData.length,
      message: 'Data Found',
      data: paginatedData
    };
  }

  private getMockJobFamilies(
    jobFamilyGroupID: string,
    pageNumber: number,
    pageSize: number,
    search?: string
  ): ApiResponse<LCMSJobFamily> {
    const mockData: LCMSJobFamily[] = [
      {
        jobFamilyID: 'SJF0003',
        jobFamilyName: 'Academic Executive - Higher Education'
      },
      {
        jobFamilyID: 'SJF0007',
        jobFamilyName: 'Academic Specialist - Higher Education'
      },
      {
        jobFamilyID: 'SJF0017',
        jobFamilyName: 'Academic Strategic Management - Higher Education'
      }
    ];

    let filteredData = mockData;
    if (search) {
      filteredData = mockData.filter(
        (item) =>
          item.jobFamilyName.toLowerCase().includes(search.toLowerCase()) ||
          item.jobFamilyID.toLowerCase().includes(search.toLowerCase())
      );
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredData.length / pageSize),
      totalRecords: filteredData.length,
      message: 'Data Found',
      data: paginatedData
    };
  }

  private getMockJobPositions(
    pageNumber: number,
    pageSize: number,
    search?: string
  ): ApiResponse<LCMSJobPosition> {
    const mockData: LCMSJobPosition[] = [
      {
        namaJabatan: 'Software Engineer'
      },
      {
        namaJabatan: 'Student Administration Services Staff'
      },
      {
        namaJabatan: 'Data Analyst'
      },
      {
        namaJabatan: 'Project Manager'
      },
      {
        namaJabatan: 'Quality Assurance Engineer'
      }
    ];

    let filteredData = mockData;
    if (search) {
      filteredData = mockData.filter((item) =>
        item.namaJabatan.toLowerCase().includes(search.toLowerCase())
      );
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredData.length / pageSize),
      totalRecords: filteredData.length,
      message: 'Data Found',
      data: paginatedData
    };
  }

  private getMockModularCourses(
    pageNumber: number,
    pageSize: number,
    search?: string
  ): ApiResponse<CourseModularData> {
    const mockData: CourseModularData[] = [
      {
        msLCMSCourseID: 'course-mod-1',
        courseName: 'Leadership Fundamentals',
        courseCode: 'LEAD001',
        deliveryType: 'modular',
        learningHours: 20,
        courseCategory: 'Leadership',
        description: 'Basic leadership skills and principles'
      },
      {
        msLCMSCourseID: 'course-mod-2',
        courseName: 'Team Management',
        courseCode: 'LEAD002',
        deliveryType: 'modular',
        learningHours: 16,
        courseCategory: 'Leadership',
        description: 'Managing teams effectively'
      }
      // ... rest of mock data
    ];

    let filteredData = mockData;
    if (search) {
      filteredData = mockData.filter(
        (item) =>
          item.courseName.toLowerCase().includes(search.toLowerCase()) ||
          item.courseCode.toLowerCase().includes(search.toLowerCase())
      );
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredData.length / pageSize),
      totalRecords: filteredData.length,
      message: 'Success',
      data: paginatedData
    };
  }

  private getMockProgramCourses(
    pageNumber: number,
    pageSize: number,
    search?: string
  ): ApiResponse<CourseProgramData> {
    // ... existing implementation
    const mockData: CourseProgramData[] = [
      {
        msLCMSCourseID: 'course-prog-1',
        courseName: 'Advanced Leadership Program',
        courseCode: 'LEAD-ADV',
        deliveryType: 'program',
        learningHours: 80,
        courseCategory: 'Leadership',
        description: 'Comprehensive leadership development program'
      }
      // ... rest of mock data
    ];

    let filteredData = mockData;
    if (search) {
      filteredData = mockData.filter(
        (item) =>
          item.courseName.toLowerCase().includes(search.toLowerCase()) ||
          item.courseCode.toLowerCase().includes(search.toLowerCase())
      );
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredData.length / pageSize),
      totalRecords: filteredData.length,
      message: 'Success',
      data: paginatedData
    };
  }

  private getMockDACUMPrograms(
    pageNumber: number,
    pageSize: number,
    search?: string
  ): ApiResponse<DACUMProgramData> {
    // ... existing implementation remains the same
    const mockData: DACUMProgramData[] = [
      {
        msDACUMProgramID: 'dacum-1',
        msLCMSCourseID: 'course-dacum-1',
        description: 'Software Development DACUM Program',
        courseName: 'Software Development Fundamentals',
        courseCode: 'SD-DACUM'
      }
      // ... rest of mock data
    ];

    let filteredData = mockData;
    if (search) {
      filteredData = mockData.filter(
        (item) =>
          item.courseName.toLowerCase().includes(search.toLowerCase()) ||
          item.courseCode.toLowerCase().includes(search.toLowerCase()) ||
          item.description.toLowerCase().includes(search.toLowerCase())
      );
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredData.length / pageSize),
      totalRecords: filteredData.length,
      message: 'Success',
      data: paginatedData
    };
  }

  private getMockEmergingNeedsCourses(
    pageNumber: number,
    pageSize: number,
    search?: string
  ): ApiResponse<CourseEmergingNeedsData> {
    const mockData: CourseEmergingNeedsData[] = [
      {
        msLCMSCourseID: 'course-emerging-1',
        courseName: 'AI and Machine Learning Fundamentals',
        courseCode: 'AI-ML-001',
        deliveryType: 'emerging needs',
        learningHours: 40,
        courseCategory: 'Emerging Technology',
        description: 'Introduction to AI and ML concepts for business applications',
        modeOfDelivery: 'Blended Learning'
      },
      {
        msLCMSCourseID: 'course-emerging-2',
        courseName: 'Digital Transformation Strategy',
        courseCode: 'DT-STRAT-001',
        deliveryType: 'emerging needs',
        learningHours: 24,
        courseCategory: 'Digital Strategy',
        description: 'Strategic approaches to digital transformation',
        modeOfDelivery: 'Virtual Conference'
      },
      {
        msLCMSCourseID: 'course-emerging-3',
        courseName: 'Cybersecurity Essentials',
        courseCode: 'CYBER-ESS-001',
        deliveryType: 'emerging needs',
        learningHours: 32,
        courseCategory: 'Security',
        description: 'Essential cybersecurity practices for modern organizations',
        modeOfDelivery: 'Hybrid Learning'
      },
      {
        msLCMSCourseID: 'course-emerging-4',
        courseName: 'Cloud Computing Fundamentals',
        courseCode: 'CLOUD-101',
        deliveryType: 'emerging needs',
        learningHours: 28,
        courseCategory: 'Cloud Technology',
        description: 'Introduction to cloud computing platforms and services',
        modeOfDelivery: 'Self Learning'
      },
      {
        msLCMSCourseID: 'course-emerging-5',
        courseName: 'Data Analytics for Business',
        courseCode: 'DATA-BIZ-001',
        deliveryType: 'emerging needs',
        learningHours: 36,
        courseCategory: 'Data Science',
        description: 'Using data analytics to drive business decisions',
        modeOfDelivery: 'Blended Learning'
      }
    ];

    let filteredData = mockData;
    if (search) {
      filteredData = mockData.filter(
        (item) =>
          item.courseName.toLowerCase().includes(search.toLowerCase()) ||
          item.courseCode.toLowerCase().includes(search.toLowerCase()) ||
          item.description.toLowerCase().includes(search.toLowerCase())
      );
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredData.length / pageSize),
      totalRecords: filteredData.length,
      message: 'Success',
      data: paginatedData
    };
  }
}

export const coursePlanService = new CoursePlanService();
